import usePrevious from '@liveauctioneers/caterwaul-components/lib/hooks/usePrevious';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { debounce, uniqBy } from 'es-toolkit';
import * as React from 'react';
import isEqual from 'react-fast-compare';
import styled, { css } from 'styled-components';

import { generateDiscoverLinks } from '@/redux/modules/discoverItems';
import { CategoryLink } from '@/types/CategoryLink';
import { ItemFacet, ItemFacets } from '@/types/ItemFacets';
import { getCategoryLevel } from '@/utils/categoryGrabber';
import { getSimilarItemsUrl } from '@/utils/urls';

const defaultTags = [
    {
        key: 'navigation.art',
        link: '/c/art/1/',
        title: <FormattedMessage id="navigation.art" />,
    },
    {
        key: 'navigation.jewelry',
        link: '/c/jewelry/6/',
        title: <FormattedMessage id="navigation.jewelry" />,
    },
    {
        key: 'navigation.asian_antiques',
        link: '/c/asian/158/',
        title: <FormattedMessage id="navigation.asian_antiques" />,
    },
    {
        key: 'navigation.furniture',
        link: '/c/furniture/4/',
        title: <FormattedMessage id="navigation.furniture" />,
    },
    {
        key: 'navigation.collectibles',
        link: '/c/collectibles/2/',
        title: <FormattedMessage id="navigation.collectibles" />,
    },
    {
        key: 'navigation.coins',
        link: '/c/coins-currency-and-stamps/24/',
        title: <FormattedMessage id="navigation.coins" />,
    },
    {
        key: 'navigation.home_and_decor',
        link: '/c/home-and-decor/5/',
        title: <FormattedMessage id="navigation.home_and_decor" />,
    },
    {
        key: 'navigation.fashion',
        link: '/c/fashion/3/',
        title: <FormattedMessage id="navigation.fashion" />,
    },
    {
        key: 'navigation.explore_categories',
        link: '/c/',
        title: <FormattedMessage id="navigation.explore_categories" />,
    },
];

type Props = {
    categoryLinks?: CategoryLink[];
    className?: string;
    facets?: ItemFacets;
    getTagItems?: Function;
    isInHeader?: boolean;
    suggestions?: any[];
    width?: number;
};

const DiscoveryTags = ({
    categoryLinks = [],
    className = '',
    facets,
    getTagItems,
    isInHeader = false,
    suggestions = [],
    width,
}: Props) => {
    let containerRef = React.useRef<HTMLDivElement>(null);
    let scrollerRef = React.useRef<HTMLDivElement>(null);

    const [containerWidth, setContainerWidth] = React.useState(1000);
    const [left, setLeft] = React.useState(0);
    const [scrollerWidth, setScrollerWidth] = React.useState(1000);
    const [selectedTag, setSelectedTag] = React.useState('');

    const handleScrollFunc = () => {
        if (scrollerRef) {
            setLeft(scrollerRef?.current?.scrollLeft);
        }
    };

    const handleScroll = debounce(handleScrollFunc, 10);

    // Implements scroll and ui logic on every render
    React.useEffect(() => {
        if (scrollerRef.current) {
            scrollerRef.current.addEventListener('scroll', handleScroll);
            handleScroll();
            updateContainerWidth();
        }
    });

    const updateContainerWidth = React.useCallback(() => {
        let newContainerWidth = containerWidth;
        let newScrollerWidth = scrollerWidth;
        if (containerRef.current) {
            newContainerWidth = containerRef.current.getBoundingClientRect()?.width;
        }

        if (scrollerRef.current) {
            newScrollerWidth = getScrollerWidth();
        }
        setContainerWidth(newContainerWidth);
        setScrollerWidth(newScrollerWidth);
    }, [containerWidth, scrollerWidth]);

    const previousWidth = usePrevious(width);
    const previousFacets = usePrevious(facets);
    const previousSuggestions = usePrevious(suggestions);

    React.useEffect(() => {
        if (previousWidth !== width || !isEqual(previousFacets, facets) || !isEqual(previousSuggestions, suggestions)) {
            updateContainerWidth();
            setSelectedTag('');
        }
    }, [facets, previousFacets, previousSuggestions, previousWidth, suggestions, updateContainerWidth, width]);

    React.useEffect(() => {
        const scrollerRefCopy = scrollerRef.current;
        return () => {
            if (scrollerRefCopy) {
                scrollerRefCopy.removeEventListener('scroll', handleScroll);
            }
        };
    }, [handleScroll]);

    const getScrollerWidth = () => {
        return (
            Array.from(scrollerRef.current.children).reduce((total, card: HTMLAnchorElement) => {
                return total + card.offsetWidth;
            }, 0) +
            (scrollerRef.current.children.length - 1) * 8
        ); // this last bit is for the margins
    };

    const mapLinks = ({ key, link, mainItem, secondItem, title }: any) => {
        const catalogIds = [`${mainItem?.categoryId}`, `${secondItem?.categoryId}`];
        if (!key || !title) {
            return null;
        }

        return (
            <BodySecondary key={key}>
                <TagLink
                    $useInHeaderStyle={isInHeader}
                    $useSelectedStyle={title === selectedTag}
                    onClick={(event) => {
                        if (!isInHeader) {
                            event.preventDefault();
                            getTagItems(catalogIds, title);
                            setSelectedTag(title);
                        }
                    }}
                    route
                    to={link}
                >
                    {title}
                </TagLink>
            </BodySecondary>
        );
    };

    // copied and refactored from DiscoverItems.js
    const createCategoryLandingLink = React.useCallback((mainItem: ItemFacet, secondItem: ItemFacet) => {
        const mainCategoryLevel = getCategoryLevel(mainItem);
        const secondCategoryLevel = getCategoryLevel(secondItem);
        const isTwoItems = secondCategoryLevel.categoryUrl;

        const key = isTwoItems
            ? `${mainCategoryLevel.categoryLabel}-${secondCategoryLevel.categoryLabel}`
            : `${mainCategoryLevel.categoryLabel}`;

        const link = isTwoItems
            ? `/c/${mainCategoryLevel.categoryUrl}/${secondCategoryLevel.categoryUrl}/${mainCategoryLevel.categoryId}/${secondCategoryLevel.categoryId}/`
            : `/c/${mainCategoryLevel.categoryUrl}/${mainCategoryLevel.categoryId}/`;

        const title = isTwoItems
            ? `${secondCategoryLevel.categoryLabel} ${mainCategoryLevel.categoryLabel}`
            : `${mainCategoryLevel.categoryLabel}`;

        return { key, link, mainCategoryLevel, secondCategoryLevel, title };
    }, []);

    const mapSuggestions = ({ label: title, value }: any, index: number) => {
        const link = getSimilarItemsUrl(value);
        return { key: `${index}-${value}`, link, title };
    };

    const suggestionLinks = suggestions.map(mapSuggestions);
    const facetLinks = generateDiscoverLinks(facets, createCategoryLandingLink);

    let linkObjects = [...suggestionLinks, ...categoryLinks, ...facetLinks];
    if (!linkObjects.length) {
        linkObjects = defaultTags;
    }

    const tags = uniqBy(linkObjects, (item) => item.link);

    return (
        <DiscoveryTagsDiv
            className={className}
            ref={containerRef}
        >
            <TagsDiv ref={scrollerRef}>{tags.map(mapLinks)}</TagsDiv>
            <LeftFadeDiv $useShowFadeStyles={left > 5} />
            <RightFadeDiv $useShowFadeStyles={left + containerWidth + 5 < scrollerWidth} />
        </DiscoveryTagsDiv>
    );
};

export default React.memo<Props>(DiscoveryTags, isEqual);

const DiscoveryTagsDiv = styled.div`
    position: relative;
    z-index: 1;
    padding: 0;
    height: 38px;
    overflow: hidden;
    margin: 16px 0 21px;
`;

const SelectedStyle = css`
    background-color: ${({ theme }) => theme.colors.grey400};
    color: ${({ theme }) => theme.colors.blue200};
    text-decoration: none;
`;

const TagLink = styled(Link)<{ $useInHeaderStyle; $useSelectedStyle }>`
    &&& {
        background-color: ${({ theme }) => theme.colors.grey500};
        border-radius: 2px;
        height: 36px;
        display: inline-flex;
        padding: 0 8px;
        align-items: center;
        letter-spacing: 0.5px;
        text-decoration: none;
        box-shadow: 0 0 2px ${({ theme }) => theme.colors.shadowOverlay200};
        margin-right: 15px !important;
        color: ${({ theme }) => theme.colors.grey100};

        &:first-child {
            margin-left: 1px;
        }

        &:last-child {
            margin-right: 4px;
        }

        &:hover,
        &:focus {
            ${SelectedStyle}
        }

        ${({ $useSelectedStyle }) => $useSelectedStyle && SelectedStyle}
        ${({ $useInHeaderStyle }) =>
            $useInHeaderStyle &&
            css`
                &&& {
                    box-shadow: none;
                    background-color: transparent;
                    padding: 0;
                    margin: 0 !important;
                }
            `}
    }
`;

const TagsDiv = styled.div`
    &&& {
        margin: 0;
        width: 100%;
        display: inline-block;
        white-space: nowrap;
        overflow: -moz-scrollbars-none;
        -ms-overflow-style: none;
        overflow-x: auto;
        overflow-y: hidden;
        height: 55px;
        padding-top: 1px;
    }

    ::-webkit-scrollbar {
        display: none !important;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin-top: 7px;
    }
`;

const LeftFadeDiv = styled.div<{ $useShowFadeStyles: boolean }>`
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 40px;
    background: linear-gradient(270deg, rgba(255, 255, 255, 0) 0%, ${({ theme }) => theme.colors.white} 100%);
    display: none;
    ${({ $useShowFadeStyles }) =>
        $useShowFadeStyles &&
        css`
            display: block;
        `}
`;

const RightFadeDiv = styled.div<{ $useShowFadeStyles: boolean }>`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 40px;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, ${({ theme }) => theme.colors.white} 100%);
    display: none;
    ${({ $useShowFadeStyles }) =>
        $useShowFadeStyles &&
        css`
            display: block;
        `}
`;
